import React from "react";
import {
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AdImageViewer from "~src/components/AdImageViewer";
import Iconify from "~src/components/Iconify";
import { PATH_BRANDS } from "~src/routes/paths";
import { Product } from "~src/types/winningProducts";
import { truncateText } from "~src/utils/string";
import BrandAvatar from "~src/components/BrandAvatar";

export const WinningProductTile = ({
  product,
}: {
  product: Product;
}) => {
  const isLargeScreen = useMediaQuery("(min-width: 1500px)");
  const theme = useTheme();
  const navigate = useNavigate();

  const handleNavigateToBrandProducts = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(PATH_BRANDS.products(Number(product.brand_id)));
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleNavigateToBrandProducts(e);
  };

  return (
    <Stack
      onClick={handleNavigateToBrandProducts}
      sx={{
        display: "flex",
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: "12px",
        backgroundColor: theme.palette.common.white,
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Box
        sx={{
          height: 64,
          width: "100%",
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          p: 2,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {product.brand_profile_image && (
          <Box
            onClick={(e) => e.stopPropagation()}
            component={RouterLink}
            to={PATH_BRANDS.products(Number(product.brand_id))}
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <BrandAvatar
              brandName={product.brand_name}
              profileImage={product.brand_profile_image}
              size={32}
              sx={{
                border: `1px solid ${theme.palette.grey[300]}`,
                mr: 2,
              }}
            />
            <Typography variant="subtitle2">
              {product.brand_name}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 2,
          height: 135,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box
          sx={{
            color: "text.primary",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          <Typography variant="h6">
            {truncateText(product.product_name, 60)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              color: "text.primary",
              textDecoration: "none",
              mt: 2,
              cursor: "pointer",
            }}
          >
            <Chip
              label={`$${Number(product.min_price).toFixed(2)} to $${Number(product.max_price).toFixed(2)}`}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
          </Box>
          {product.product_url && (
            <Box
              onClick={(e) => e.stopPropagation()}
              component={RouterLink}
              to={product.product_url}
              target="_blank"
              sx={{
                color: "text.primary",
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              <Iconify
                icon="tabler:external-link"
                sx={{ height: 24, width: 24 }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box
        onClick={handleImageClick}
        sx={{
          width: "100%",
          height: isLargeScreen ? 500 : 350,
          p: 2,
          cursor: "pointer",
        }}
      >
        <AdImageViewer sourceUrl={product.product_image_url || ""} />
      </Box>

    </Stack>
  );
};